/**
 * Les trois murs — eventmatch.pro/mur/<slug>.
 *
 * Conçu pour deux usages à la fois, sans compromis pour aucun :
 *   · projeté sur un écran en salle — grandes colonnes, défilement continu,
 *     rafraîchissement discret ;
 *   · ouvert sur un téléphone — les colonnes deviennent des sections, tout
 *     reste cliquable (chaque nom mène à la page de la personne).
 *
 * Le visiteur connecté voit en plus ses actions du jour : ce qu'il peut faire
 * maintenant pour monter dans le classement, avec les points en face.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Handshake, Search, Sparkles, Trophy, Users } from 'lucide-react';
import { appelerApi } from '../reseau.ts';
import { useLibelles } from '../i18n.tsx';
import { Marque } from '../composants/Marque.tsx';
import { Etoile } from '../composants/Etoile.tsx';
import { PanneauActions } from './compte/PanneauActions.tsx';
import { nomDe, type Deal, type Espace, type Murs as DonneesMurs, type RangMur, type Recherche, type Vignette } from './compte/types.ts';

/** Toutes les dix secondes : sur un mur, c'est indiscernable du temps réel. */
const RAFRAICHISSEMENT_MS = 10_000;

export function Murs({ slug }: { slug: string }) {
  const { t } = useLibelles();
  const [donnees, setDonnees] = useState<DonneesMurs | null | undefined>(undefined);
  const [compte, setCompte] = useState<Espace | null | undefined>(undefined);
  const [plein, setPlein] = useState(() => new URLSearchParams(location.search).get('ecran') === '1');

  const charger = useCallback(async () => {
    const r = await appelerApi<DonneesMurs>(`/api/murs/${encodeURIComponent(slug)}`);
    setDonnees(r.donnees ?? null);
  }, [slug]);

  useEffect(() => {
    void charger();
    const minuterie = setInterval(() => void charger(), RAFRAICHISSEMENT_MS);
    return () => clearInterval(minuterie);
  }, [charger]);

  useEffect(() => {
    void appelerApi<Espace>('/api/compte/moi').then((r) => setCompte(r.donnees ?? null));
  }, []);

  if (donnees === undefined) return <p className="min-h-screen bg-void p-10 text-fumee">{t('app.chargement', 'Chargement…')}</p>;
  if (donnees === null) {
    return (
      <div className="min-h-screen bg-void p-10 text-fumee">
        <p>{t('murs.introuvable', 'Ce mur n’existe pas — l’événement n’est peut-être plus public.')}</p>
        <a href="/" className="mt-4 inline-block text-blanc underline">EventMatch.pro</a>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-void font-sans text-blanc">
      <header className={`mx-auto flex max-w-[1800px] flex-wrap items-center justify-between gap-4 px-6 ${plein ? 'py-4' : 'py-5'}`}>
        <div className="flex items-center gap-4">
          <a href="/" className="flex items-center gap-2.5">
            <Marque taille={plein ? 26 : 20} />
            <span className={`font-medium ${plein ? 'text-[19px]' : 'text-[15px]'}`}>EventMatch<span className="text-fumee">.pro</span></span>
          </a>
          <span className="hidden h-6 w-px bg-graphite sm:block" />
          <a href={`/evenements/${donnees.evenement.slug}`} className={`truncate ${plein ? 'text-[19px]' : 'text-[15px]'} hover:underline`}>
            {donnees.evenement.nom}
          </a>
        </div>
        <div className="flex flex-wrap items-center gap-5 text-[13px] text-fumee">
          <span className="inline-flex items-center gap-1.5"><Users size={14} strokeWidth={1.5} /> {donnees.compteurs.participants} {t('murs.participants', 'participants')}</span>
          <span className="inline-flex items-center gap-1.5"><Handshake size={14} strokeWidth={1.5} /> {donnees.compteurs.deals} {t('murs.deals', 'deals')}</span>
          <button onClick={() => setPlein(!plein)} className="rounded-full border border-graphite px-3 py-1 hover:text-blanc">
            {plein ? t('murs.quitter_ecran', 'Quitter le mode écran') : t('murs.mode_ecran', 'Mode écran')}
          </button>
        </div>
      </header>

      {!plein && compte && <PanneauActions evenementId={donnees.evenement.id} slug={donnees.evenement.slug} />}

      <main className={`mx-auto grid max-w-[1800px] gap-5 px-6 pb-10 lg:grid-cols-3 ${plein ? 'h-[calc(100vh-88px)]' : ''}`}>
        <Colonne titre={t('murs.deal', 'Deal Wall')} sous={t('murs.deal_sous', 'Ce qui vient de se conclure')} icone={<Handshake size={16} strokeWidth={1.5} />} plein={plein} vide={t('murs.deal_vide', 'Aucun deal pour l’instant. Après votre rendez-vous, confirmez-le dans l’app : vous apparaîtrez ici.')} nombre={donnees.deals.length}>
          {donnees.deals.map((d) => <CarteDeal key={d.id} deal={d} plein={plein} t={t} />)}
        </Colonne>

        <Colonne titre={t('murs.seek', 'Seek Wall')} sous={t('murs.seek_sous', 'Ce que chacun cherche')} icone={<Search size={16} strokeWidth={1.5} />} plein={plein} defile vide={t('murs.seek_vide', 'Personne n’a encore dit ce qu’il cherche. Complétez votre profil : vous serez le premier.')} nombre={donnees.recherches.length}>
          {donnees.recherches.map((r) => <CarteRecherche key={r.participant_id} r={r} plein={plein} t={t} />)}
        </Colonne>

        <Colonne titre={t('murs.company', 'Company Wall')} sous={t('murs.company_sous', 'Le classement du jour')} icone={<Trophy size={16} strokeWidth={1.5} />} plein={plein} defile vide={t('murs.company_vide', 'Le classement se remplit à mesure que les profils se complètent.')} nombre={donnees.classement.length}>
          {donnees.classement.map((r) => <LigneClassement key={r.personne_id} r={r} plein={plein} />)}
        </Colonne>
      </main>
    </div>
  );
}

/**
 * Une colonne. Quand elle défile, la liste est dupliquée et translatée en
 * continu — et l'animation s'arrête au survol, sinon on ne pourrait rien
 * cliquer. Sans mouvement si le visiteur a demandé moins d'animations.
 */
function Colonne({
  titre, sous, icone, children, plein, defile = false, vide, nombre,
}: {
  titre: string; sous: string; icone: React.ReactNode; children: React.ReactNode;
  plein: boolean; defile?: boolean; vide: string; nombre: number;
}) {
  const conteneur = useRef<HTMLDivElement>(null);
  const [hauteur, setHauteur] = useState(0);

  useEffect(() => {
    if (!defile || !conteneur.current) return;
    setHauteur(conteneur.current.scrollHeight / 2);
  }, [defile, children]);

  const animer = defile && plein && hauteur > 400;

  return (
    <section className={`flex min-h-0 flex-col rounded-[14px] border border-graphite bg-charbon ${plein ? 'overflow-hidden' : ''}`}>
      <div className="flex items-baseline justify-between gap-3 border-b border-graphite px-5 py-4">
        <div>
          <p className={`flex items-center gap-2 font-medium ${plein ? 'text-[22px]' : 'text-[17px]'}`}>{icone} {titre}</p>
          <p className="mt-0.5 text-[12px] text-fumee">{sous}</p>
        </div>
        <span className="font-serif text-[20px] text-fumee">{nombre}</span>
      </div>

      <div className={`min-h-0 flex-1 ${plein ? 'overflow-hidden' : ''} px-4 py-3`}>
        {nombre === 0 ? (
          <p className="max-w-[420px] py-6 text-[14px] leading-relaxed text-fumee">{vide}</p>
        ) : (
          <div
            ref={conteneur}
            className={`flex flex-col gap-3 ${animer ? 'defilement-mur hover:[animation-play-state:paused]' : ''}`}
            style={animer ? { ['--defilement' as string]: `${hauteur}px`, animationDuration: `${Math.max(20, nombre * 4)}s` } : undefined}
          >
            {children}
            {animer && <div aria-hidden className="flex flex-col gap-3">{children}</div>}
          </div>
        )}
      </div>
    </section>
  );
}

/** Un nom, cliquable s'il a une page — c'est la règle sur les trois murs. */
function Nom({ v, plein }: { v: Vignette; plein: boolean }) {
  const contenu = (
    <>
      {v.entreprise ?? nomDe(v)} <Etoile premium={v.premium} texte={false} />
    </>
  );
  const classe = `font-medium ${plein ? 'text-[17px]' : 'text-[14px]'} text-blanc`;
  return v.pseudo ? (
    <a href={`/${v.pseudo}`} className={`${classe} hover:underline`}>{contenu}</a>
  ) : (
    <span className={classe}>{contenu}</span>
  );
}

function CarteDeal({ deal, plein, t }: { deal: Deal; plein: boolean; t: (c: string, d?: string) => string }) {
  return (
    <article className="rounded-[10px] bg-void p-4">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Nom v={deal.a} plein={plein} />
        <span className="text-match-gradient font-serif text-[18px]">×</span>
        <Nom v={deal.b} plein={plein} />
      </p>
      {deal.objet && <p className={`mt-1.5 ${plein ? 'text-[15px]' : 'text-[13px]'} leading-relaxed text-blanc/80`}>{deal.objet}</p>}
      <p className="mt-2 flex flex-wrap items-center gap-x-2 text-[12px] text-fumee">
        <Sparkles size={11} strokeWidth={1.5} />
        {t('murs.deal_conclu', 'On va travailler ensemble')}
        {deal.recommande_par && (
          <span>· {t('murs.presente_par', 'présentés par')} {deal.recommande_par.pseudo ? <a href={`/${deal.recommande_par.pseudo}`} className="text-blanc hover:underline">{nomDe(deal.recommande_par)}</a> : nomDe(deal.recommande_par)}</span>
        )}
      </p>
    </article>
  );
}

function CarteRecherche({ r, plein, t }: { r: Recherche; plein: boolean; t: (c: string, d?: string) => string }) {
  const lignes = [
    r.client_ideal && { libelle: t('murs.client_ideal', 'Client idéal'), texte: r.client_ideal },
    r.partenaire_ideal && { libelle: t('murs.partenaire_ideal', 'Partenaire idéal'), texte: r.partenaire_ideal },
    r.pour_quoi_faire && { libelle: t('murs.pour_quoi', 'Pour faire ensemble'), texte: r.pour_quoi_faire },
    !r.client_ideal && !r.partenaire_ideal && !r.pour_quoi_faire && r.recherche && { libelle: t('murs.cherche', 'Cherche'), texte: r.recherche },
  ].filter(Boolean) as { libelle: string; texte: string }[];

  return (
    <article className="rounded-[10px] bg-void p-4">
      <p className="flex flex-wrap items-center gap-2">
        <Nom v={r} plein={plein} />
        {r.secteur && <span className="text-[12px] text-fumee">{r.secteur}</span>}
      </p>
      <dl className="mt-2 flex flex-col gap-1.5">
        {lignes.slice(0, 3).map((l) => (
          <div key={l.libelle}>
            <dt className="label-caps text-fumee">{l.libelle}</dt>
            <dd className={`${plein ? 'text-[15px]' : 'text-[13px]'} leading-relaxed text-blanc/85`}>{l.texte}</dd>
          </div>
        ))}
      </dl>
    </article>
  );
}

function LigneClassement({ r, plein }: { r: RangMur; plein: boolean }) {
  return (
    <div className="flex items-center gap-3 rounded-[10px] bg-void px-4 py-2.5">
      <span className={`w-8 shrink-0 font-serif ${plein ? 'text-[22px]' : 'text-[17px]'} ${r.rang <= 3 ? 'text-match-gradient' : 'text-fumee'}`}>{r.rang}</span>
      {r.photo_url ? (
        <img src={r.photo_url} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
      ) : (
        <span className="h-8 w-8 shrink-0 rounded-full border border-graphite" />
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate"><Nom v={r} plein={plein} /></span>
        {r.secteur && <span className="block truncate text-[12px] text-fumee">{r.secteur}</span>}
      </span>
      <span className="shrink-0 text-right">
        <span className={`block font-serif ${plein ? 'text-[20px]' : 'text-[16px]'} text-blanc`}>{r.influence}</span>
        {r.points_jour > 0 && <span className="text-match-gradient block text-[11px]">+{r.points_jour} aujourd’hui</span>}
      </span>
    </div>
  );
}
